import type { ThemeConfig } from 'antd';

// FlightPath design system - "Linear backbone, bold personality"
export const colors = {
  // Sidebar
  sidebarBg: '#1A1A2E',
  sidebarHover: '#252542',
  sidebarActive: '#2A2A4A',

  // Content
  contentBg: '#F8F9FC',
  surfaceBg: '#FFFFFF',
  surfaceBorder: '#E2E4EA',

  // Primary
  primary: '#4739E6',
  primaryDark: '#3a2eb8',
  primaryLight: '#6b5fee',

  // Accent / semantic
  success: '#00C853',
  warning: '#FF6D00',
  error: '#FF1744',
  info: '#2979FF',
  completed: '#00BFA5',
  // Release-gate accent: the Training checkpoint marker (cool→warm gate ramp).
  violet: '#9333EA',

  // Neutrals
  textPrimary: '#0F172A',
  textSecondary: '#64748B',
  textTertiary: '#94A3B8',
  textOnDark: '#E2E8F0',
  textOnDarkMuted: '#94A3B8',

  // Legacy compat — TODO: remove after migrating all consumers to the semantic names above
  purple: '#4739E6',
  blue: '#59CDFF',
} as const;

// Tinted bg/fg pairs for category chips (stale-reason strip, rank-override cell).
// Centralised here so chip tints stay consistent across components and remain
// the single point of change when a future dark-mode pass needs to adjust them.
export const chipColors = {
  info: { bg: '#E6F4FF', fg: '#0050B3' },
  warning: { bg: '#FFF2E6', fg: '#C55800', border: '#FFD6B3' },
  // 'attention' uses a softer orange border for the StatusStrip outline,
  // distinct from the override-chip border above.
  attentionBorder: '#FFD9B3',
  purple: { bg: '#F1E5FF', fg: '#5915C7' },
  // Mono palette for tabular figures in the rank cell.
  mono: {
    fg: '#4A5168',
    fgMuted: '#7B8194',
    bg: '#EEF0F5',
  },
} as const;

// Backwards-compatible alias — TODO: remove after migrating all consumers to 'colors'
export const flyrColors = {
  purple: colors.primary,
  blue: colors.blue,
  purpleDark: colors.primaryDark,
  purpleLight: colors.primaryLight,
  blueDark: '#2ea3cc',
  blueLight: '#8cdbff',
  gray: {
    50: '#F8F9FC',
    100: '#F1F5F9',
    200: '#E2E4EA',
    300: '#CBD5E1',
    400: '#94A3B8',
    500: '#64748B',
    600: '#475569',
    700: '#334155',
    800: '#1E293B',
    900: '#0F172A',
  },
  success: colors.success,
  warning: colors.warning,
  error: colors.error,
  info: colors.info,
} as const;

// Spacing - compact density (Linear-like)
export const spacing = {
  xs: 4,
  sm: 8,
  md: 16,
  lg: 20,
  xl: 24,
  xxl: 32,
} as const;

// Typography - bold, confident hierarchy
export const typography = {
  h1: { fontSize: 32, fontWeight: 700, lineHeight: 1.2 },
  h2: { fontSize: 24, fontWeight: 600, lineHeight: 1.3 },
  h3: { fontSize: 18, fontWeight: 600, lineHeight: 1.4 },
  h4: { fontSize: 16, fontWeight: 600, lineHeight: 1.5 },
  h5: { fontSize: 14, fontWeight: 600, lineHeight: 1.5 },
  body: { fontSize: 14, fontWeight: 400, lineHeight: 1.6 },
  bodyLarge: { fontSize: 16, fontWeight: 400, lineHeight: 1.6 },
  bodySmall: { fontSize: 13, fontWeight: 400, lineHeight: 1.5 },
  caption: { fontSize: 12, fontWeight: 400, lineHeight: 1.5 },
  stat: { fontSize: 36, fontWeight: 700, lineHeight: 1.1 },
  statLarge: { fontSize: 48, fontWeight: 700, lineHeight: 1.0 },
  mono: { fontSize: 13, fontWeight: 400, lineHeight: 1.5 },
  sectionLabel: { fontSize: 11, fontWeight: 600, lineHeight: 1.5, letterSpacing: '0.05em' },
} as const;

// Component sizes
export const sizes = {
  card: {
    default: { padding: spacing.md },
    compact: { padding: spacing.sm },
    tight: { padding: spacing.xs },
  },
  button: {
    large: { height: 40, fontSize: 14 },
    default: { height: 32, fontSize: 14 },
    small: { height: 24, fontSize: 12 },
  },
  sidebar: {
    expanded: 220,
    collapsed: 60,
  },
  table: {
    rowHeight: 40,
  },
} as const;

// CSS custom properties
export const cssVariables = {
  '--fp-sidebar-bg': colors.sidebarBg,
  '--fp-content-bg': colors.contentBg,
  '--fp-surface-bg': colors.surfaceBg,
  '--fp-surface-border': colors.surfaceBorder,
  '--fp-primary': colors.primary,
  '--fp-text-primary': colors.textPrimary,
  '--fp-text-secondary': colors.textSecondary,
  '--fp-text-tertiary': colors.textTertiary,
} as const;

// Ant Design theme configuration
export const antdTheme: ThemeConfig = {
  token: {
    // Primary colors
    colorPrimary: colors.primary,
    colorSuccess: colors.success,
    colorWarning: colors.warning,
    colorError: colors.error,
    colorInfo: colors.info,

    // Border radius - slightly sharp
    borderRadius: 6,
    borderRadiusLG: 8,
    borderRadiusSM: 4,
    borderRadiusXS: 2,

    // Typography - Inter
    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    fontSize: 14,
    fontSizeLG: 16,
    fontSizeSM: 12,
    fontSizeXL: 20,

    // Layout - compact
    padding: 16,
    paddingLG: 20,
    paddingSM: 12,
    paddingXS: 8,

    // Colors
    colorBgContainer: colors.surfaceBg,
    colorBgElevated: colors.surfaceBg,
    colorBgLayout: colors.contentBg,
    colorBorder: colors.surfaceBorder,
    colorBorderSecondary: '#F1F5F9',

    // Text
    colorText: colors.textPrimary,
    colorTextSecondary: colors.textSecondary,
    colorTextTertiary: colors.textTertiary,
    colorTextQuaternary: '#CBD5E1',

    // Links
    colorLink: colors.primary,
    colorLinkHover: colors.primaryDark,
    colorLinkActive: colors.primaryDark,

    // Controls
    controlHeight: 32,
    controlHeightLG: 40,
    controlHeightSM: 28,
  },

  components: {
    Button: {
      borderRadius: 6,
      fontWeight: 500,
      primaryShadow: 'none',
    },
    Card: {
      borderRadius: 8,
      paddingLG: 16,
      boxShadow: 'none',
    },
    Input: {
      borderRadius: 6,
      paddingBlock: 6,
    },
    Table: {
      borderRadius: 8,
      headerBg: colors.contentBg,
      headerColor: colors.textSecondary,
      rowHoverBg: '#F8F9FC',
      cellPaddingBlock: 10,
    },
    Modal: {
      borderRadius: 8,
    },
    Menu: {
      borderRadius: 6,
      itemSelectedBg: `${colors.primary}10`,
      itemSelectedColor: colors.primary,
      itemHoverBg: `${colors.primary}05`,
    },
    Tabs: {
      itemSelectedColor: colors.primary,
      itemHoverColor: colors.primaryLight,
      inkBarColor: colors.primary,
    },
    Tag: {
      borderRadiusSM: 4,
    },
    Statistic: {
      contentFontSize: 28,
    },
  },

  algorithm: undefined,
};

// Utility: color with opacity
export const getColorWithOpacity = (color: string, opacity: number) => {
  const hex = color.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

// Utility: gradient styles (kept for backward compat)
export const getGradientStyle = (type: 'primary' | 'purple' | 'blue' = 'primary') => {
  const gradients = {
    primary: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.blue} 100%)`,
    purple: `linear-gradient(135deg, ${colors.primaryDark} 0%, ${colors.primaryLight} 100%)`,
    blue: `linear-gradient(135deg, #2ea3cc 0%, #8cdbff 100%)`,
  };
  return { background: gradients[type] };
};

export const getShadowStyle = (type: 'primary' | 'blue' = 'primary') => {
  const shadows = {
    primary: `0 1px 3px rgba(0,0,0,0.08)`,
    blue: `0 1px 3px rgba(89,205,255,0.1)`,
  };
  return { boxShadow: shadows[type] };
};

// Apply CSS variables to document
export const applyCSSVariables = () => {
  if (typeof document !== 'undefined') {
    const root = document.documentElement;
    Object.entries(cssVariables).forEach(([property, value]) => {
      root.style.setProperty(property, value);
    });
  }
};
